<template>
  <v-dialog v-model="internalValue" max-width="650" scrollable transition="dialog-bottom-transition"
    content-class="rounded-md overflow-hidden">
    <v-card class="rounded-md overflow-hidden bg-gray-50 flex flex-col max-h-[85vh]">
      <!-- Header -->
      <div
        class="bg-gradient-to-r from-indigo-700 via-indigo-600 to-blue-600 px-5 py-4 text-white flex items-center justify-between shrink-0 shadow-sm">
        <div class="flex items-center gap-2.5 min-w-0">
          <div class="p-2 rounded-xl bg-white/15 backdrop-blur-md shrink-0">
            <v-icon small color="white">mdi-history</v-icon>
          </div>
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <h3 class="text-sm sm:text-base font-extrabold text-white mb-0 truncate">
                {{ headerTitle }}
              </h3>
              <span
                class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-white tracking-wide uppercase shrink-0">
                {{ completedVisitsCount }} Done
              </span>
            </div>
            <p class="text-indigo-100 text-xs mb-0 truncate" v-if="customerName">
              {{ customerName }} <span v-if="caseNo">· #{{ caseNo }}</span>
            </p>
          </div>
        </div>

        <button type="button"
          class="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-all  border-none text-white cursor-pointer shrink-0"
          @click="internalValue = false">
          <v-icon small color="white">mdi-close</v-icon>
        </button>
      </div>

      <!-- Quick Metrics Summary Strip -->
      <div class="grid grid-cols-3 gap-2 px-4 py-3 bg-white border-b border-gray-100 shrink-0">
        <div class="p-2.5 rounded-xl bg-indigo-50/60 border border-indigo-100/50 flex flex-col">
          <span class="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Total Visits</span>
          <span class="text-base font-black text-indigo-700 font-mono mt-0.5">
            {{ totalVisitsCount }}
          </span>
        </div>
        <div class="p-2.5 rounded-xl bg-emerald-50/60 border border-emerald-100/50 flex flex-col">
          <span class="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Completed</span>
          <span class="text-base font-black text-emerald-700 font-mono mt-0.5">
            {{ completedVisitsCount }}
          </span>
        </div>
        <div class="p-2.5 rounded-xl bg-teal-50/60 border border-teal-100/50 flex flex-col">
          <span class="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Collected</span>
          <span class="text-base font-black text-teal-700 font-mono mt-0.5">
            ₹ {{ formattedTotalCollected }}
          </span>
        </div>
      </div>

      <!-- Main Body: Timeline List -->
      <v-card-text class="p-4 sm:p-5 overflow-y-auto space-y-3.5 flex-1">
        <!-- Loading State -->
        <div v-if="loading" class="py-12 flex flex-col items-center justify-center">
          <v-progress-circular indeterminate color="indigo" size="36"></v-progress-circular>
          <span class="text-xs text-gray-500 font-medium mt-2">Loading visit history...</span>
        </div>

        <!-- Empty State -->
        <div v-else-if="!visitList || !visitList.length"
          class="py-12 flex flex-col items-center justify-center text-center">
          <div class="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center mb-2">
            <v-icon color="grey lighten-1">mdi-calendar-blank-outline</v-icon>
          </div>
          <h4 class="text-sm font-bold text-gray-700 mb-0.5">No Visits Recorded</h4>
          <p class="text-xs text-gray-400 max-w-xs mb-0">
            No visits or meeting reports have been recorded for this case yet.
          </p>
        </div>

        <!-- Visit Cards Timeline -->
        <div v-else class="space-y-3.5 relative">
          <div v-for="(visit, idx) in visitList" :key="visit._id || visit.id || idx"
            class="bg-white rounded-2xl p-4 border border-gray-100 shadow-2xs transition-all hover:shadow-xs relative">
            <!-- Card Header: Visit Count, Date & Status -->
            <div class="flex items-center justify-between pb-2.5 border-b border-gray-100 gap-2">
              <div class="flex items-center gap-2 min-w-0">
                <span class="p-2 rounded-lg bg-indigo-50 text-indigo-700 font-black text-xs shrink-0">
                  #{{ visitList.length - idx }}
                </span>
                <div class="min-w-0">
                  <div class="flex items-center gap-1.5 text-xs text-gray-800 font-bold truncate">
                    <v-icon x-small color="grey">mdi-clock-outline</v-icon>
                    <span>{{ formatDateTime(visit.actualStartDateTime || visit.taskStartDateTime || visit.createdAt)
                    }}</span>
                  </div>
                  <span v-if="getAgentName(visit)" class="text-[11px] text-gray-400 truncate block">
                    By {{ getAgentName(visit) }}
                  </span>
                </div>
              </div>

              <span class="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase shrink-0"
                :style="{ backgroundColor: getStatusBg(visit.taskStatus || visit.status), color: getStatusText(visit.taskStatus || visit.status) }">
                {{ visit.taskStatus || visit.status || 'OPEN' }}
              </span>
            </div>

            <!-- Key Details Grid -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-3 text-xs">
              <!-- Meeting Outcome -->
              <div class="p-2.5 rounded-xl bg-gray-50 border border-gray-100 flex items-start gap-2">
                <v-icon small color="indigo" class="mt-0.5">mdi-flag-outline</v-icon>
                <div class="min-w-0 flex-1">
                  <span class="text-[10px] uppercase font-bold text-gray-400 block leading-tight">Visit Outcome</span>
                  <span class="font-bold text-gray-800 text-xs block truncate mt-0.5">
                    {{ visit.meetingDetails?.meetingOutCome || visit.userDefinedCaseStatus || visit.subCase || '-' }}
                  </span>
                </div>
              </div>

              <!-- Fund Collected -->
              <div class="p-2.5 rounded-xl bg-gray-50 border border-gray-100 flex items-start gap-2">
                <v-icon small color="emerald" class="mt-0.5">mdi-cash-check</v-icon>
                <div class="min-w-0 flex-1">
                  <span class="text-[10px] uppercase font-bold text-gray-400 block leading-tight">Fund Collected</span>
                  <span class="font-black text-emerald-700 text-xs block truncate mt-0.5 font-mono">
                    {{ visit.meetingDetails?.fundCollected ? '₹ ' + formatAmount(visit.meetingDetails.fundCollected) :
                      '-' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Notes / Remarks -->
            <div v-if="visit.meetingDetails?.meetingNotes || visit.taskDescription || visit.description"
              class="mt-2.5 p-2.5 rounded-xl bg-amber-50/60 border border-amber-100/60 text-xs">
              <div class="flex items-center gap-1.5 text-amber-800 font-bold mb-1 text-[11px]">
                <v-icon x-small color="amber darken-3">mdi-note-text-outline</v-icon>
                <span>Notes & Remarks</span>
              </div>
              <p class="text-gray-700 mb-0 leading-relaxed font-normal">
                {{ visit.meetingDetails?.meetingNotes || visit.taskDescription || visit.description }}
              </p>
            </div>

            <!-- Questionnaire / Form Responses -->
            <div v-if="hasQuestionnaire(visit)"
              class="mt-2.5 p-2.5 rounded-xl bg-blue-50/40 border border-blue-100/60 text-xs space-y-1.5">
              <div class="flex items-center gap-1.5 text-blue-900 font-bold text-[11px] mb-1">
                <v-icon x-small color="blue darken-2">mdi-clipboard-list-outline</v-icon>
                <span>Form Fields & Questionnaire</span>
              </div>
              <div v-for="(ans, aIdx) in getQuestionnaireList(visit)" :key="aIdx"
                class="flex items-center justify-between py-1 border-b border-blue-100/50 last:border-0">
                <span class="text-gray-600 font-medium">{{ ans.label }}</span>
                <span class="text-gray-900 font-bold text-right ml-2">{{ ans.value }}</span>
              </div>
            </div>

            <!-- Call Frequency Info -->
            <div v-if="Array.isArray(visit.callInfo) && visit.callInfo.length > 0"
              class="mt-2 flex items-center justify-between text-xs text-gray-500 px-1">
              <span class="flex items-center gap-1">
                <v-icon x-small color="grey">mdi-phone-log</v-icon>
                <span>Calls made on this visit:</span>
              </span>
              <span class="font-extrabold text-gray-800 font-mono">{{ visit.callInfo.length }}</span>
            </div>

            <!-- Media Attachments (Photos / Receipts / Selfie) -->
            <div v-if="getAttachments(visit).length > 0" class="mt-3 pt-2.5 border-t border-gray-100">
              <span class="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">
                Attachments & Proofs ({{ getAttachments(visit).length }})
              </span>
              <div class="flex flex-wrap gap-2">
                <div v-for="(att, attIdx) in getAttachments(visit)" :key="attIdx" @click="openMediaPreview(att)"
                  class="cursor-pointer group relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50 hover:border-indigo-400 transition-all">
                  <img v-if="isImage(att)" :src="att.url" :alt="att.name || 'Attachment'"
                    class="w-16 h-16 object-cover group-hover:scale-105 transition-transform" />
                  <div v-else class="w-16 h-16 flex flex-col items-center justify-center p-1 text-center">
                    <v-icon small color="indigo">mdi-file-document-outline</v-icon>
                    <span class="text-[9px] text-gray-600 line-clamp-1 mt-1">{{ att.name }}</span>
                  </div>
                  <div
                    class="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <v-icon x-small color="white">mdi-eye</v-icon>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Footer Action -->
      <div class="p-3.5 bg-white border-t border-gray-100 flex items-center justify-end shrink-0">
        <v-btn depressed color="grey lighten-4"
          class="grey--text text--darken-3 rounded-xl text-xs font-bold px-5 h-9 capitalize"
          @click="internalValue = false">
          Close
        </v-btn>
      </div>
    </v-card>

    <!-- Media Full Preview Lightbox Dialog -->
    <v-dialog v-model="previewDialog" max-width="500">
      <v-card v-if="selectedPreview" class="rounded-2xl overflow-hidden p-2 text-center bg-black/90">
        <div class="flex justify-end p-2">
          <v-btn icon small dark @click="previewDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <img v-if="isImage(selectedPreview)" :src="selectedPreview.url"
          class="max-h-[75vh] w-auto max-w-full rounded-xl mx-auto" />
        <div v-else class="py-8 text-white">
          <p class="text-sm font-bold mb-3">{{ selectedPreview.name }}</p>
          <v-btn small :color="$vuetify.theme.currentTheme.employeePrimary" :href="selectedPreview.url" target="_blank" class="white--text">
            Download File
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import dayjs from 'dayjs'
import { convertNumberToAmount } from '@/app/utils/helpers'
import { getCollectionCaseById } from '@/app/services/dashboard'

export default {
  name: 'AppTaskHistoryDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    task: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      fetchedCase: null,
      previewDialog: false,
      selectedPreview: null,
      s3BaseUrl: process.env.VUE_APP_S3_BASE_URL || 'https://uat-cdn.myfinfi.com/'
    }
  },
  computed: {
    internalValue: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    caseData() {
      return this.fetchedCase || this.task || {}
    },
    headerTitle() {
      return this.task?.taskTitle || (this.task?.caseNo ? `Case #${this.task.caseNo}` : 'Task History')
    },
    customerName() {
      return (
        this.caseData.customer?.customerFullName ||
        this.caseData.customer?.name ||
        this.task?.customer?.customerFullName ||
        ''
      )
    },
    caseNo() {
      return this.caseData.caseNo || this.task?.caseNo || ''
    },
    visitList() {
      const tasks = this.caseData.tasks || this.task.tasks || []
      if (Array.isArray(tasks) && tasks.length > 0) {
        return [...tasks].sort((a, b) => {
          const dateA = new Date(a.actualStartDateTime || a.taskStartDateTime || a.createdAt || 0)
          const dateB = new Date(b.actualStartDateTime || b.taskStartDateTime || b.createdAt || 0)
          return dateB - dateA
        })
      }

      // If single task object itself
      if (this.task && (this.task.meetingDetails || this.task.taskStatus || this.task.status)) {
        return [this.task]
      }

      return []
    },
    totalVisitsCount() {
      return this.visitList.length
    },
    completedVisitsCount() {
      return this.visitList.filter((v) => (v.taskStatus || v.status) === 'COMPLETED').length
    },
    formattedTotalCollected() {
      const sum = this.visitList.reduce((acc, v) => {
        const val = Number(v.meetingDetails?.fundCollected) || 0
        return acc + val
      }, 0)
      return convertNumberToAmount(sum)
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.loadDetails()
      }
    }
  },
  methods: {
    async loadDetails() {
      const caseId = this.task?.caseId || this.task?.case?._id || this.task?._id || this.task?.id
      if (!caseId) return

      try {
        this.loading = true
        const res = await getCollectionCaseById({ caseId }).catch(() => null)
        if (res?.data?.case || res?.data?.data) {
          this.fetchedCase = res.data.case || res.data.data
        }
      } catch (err) {
        console.error('Error fetching case history:', err)
      } finally {
        this.loading = false
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      return dayjs(dateStr).format('DD MMM YYYY, hh:mm A')
    },
    formatAmount(amount) {
      return convertNumberToAmount(amount)
    },
    getAgentName(visit) {
      const user = visit.assignedTo
      if (!user) return ''
      if (typeof user === 'string') return ''
      return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || user.email || ''
    },
    getStatusBg(status) {
      const s = (status || '').toUpperCase()
      if (s === 'COMPLETED' || s === 'CLOSED') return '#ECFDF5'
      if (s === 'IN_PROGRESS' || s === 'OPEN') return '#EFF6FF'
      if (s === 'MISSED' || s === 'REJECTED') return '#FEF2F2'
      return '#FFFBEB'
    },
    getStatusText(status) {
      const s = (status || '').toUpperCase()
      if (s === 'COMPLETED' || s === 'CLOSED') return '#047857'
      if (s === 'IN_PROGRESS' || s === 'OPEN') return '#1D4ED8'
      if (s === 'MISSED' || s === 'REJECTED') return '#B91C1C'
      return '#B45309'
    },
    hasQuestionnaire(visit) {
      const q = visit.meetingDetails?.questionnaire
      return Array.isArray(q) && q.length > 0
    },
    getQuestionnaireList(visit) {
      const q = visit.meetingDetails?.questionnaire || []
      return q
        .map((item) => {
          const label = item.question || item.title || item.label || 'Question'
          let value = item.answer || item.value || item.selectedOption || '-'
          if (typeof value === 'object') {
            value = JSON.stringify(value)
          }
          return { label, value }
        })
        .filter((item) => item.value !== '-')
    },
    getAttachments(visit) {
      const list = []
      const md = visit.meetingDetails || {}

      if (Array.isArray(md.attachments)) {
        md.attachments.forEach((a) => {
          const rawUrl = a.url || a.filePath || a.s3Url || a.s3_url
          if (rawUrl) {
            const url = rawUrl.startsWith('http') ? rawUrl : `${this.s3BaseUrl.replace(/\/$/, '')}/${rawUrl.replace(/^\//, '')}`
            list.push({
              name: a.fileName || a.name || 'Document',
              url,
              type: a.type || 'img'
            })
          }
        })
      }

      // Check selfie in additionalInfo
      const selfie = visit.additionalInfo?.selfie?.doc_physical_path || visit.selfieUrl
      if (selfie) {
        const url = selfie.startsWith('http') ? selfie : `${this.s3BaseUrl.replace(/\/$/, '')}/${selfie.replace(/^\//, '')}`
        list.push({
          name: 'Selfie Proof',
          url,
          type: 'img'
        })
      }

      return list
    },
    isImage(att) {
      if (!att || !att.url) return false
      const url = att.url.toLowerCase()
      return url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.png') || url.endsWith('.webp') || url.endsWith('.gif') || att.type === 'img'
    },
    openMediaPreview(att) {
      this.selectedPreview = att
      this.previewDialog = true
    }
  }
}
</script>
